import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserRole } from "@/hooks/useUserRole";
import { AppSeo } from "@/components/AppSeo";
import {
  deleteExcerptusPrompt,
  fetchAllExcerptusPrompts,
  type ExcerptusPrompt,
} from "@/lib/composerPrompts";
import { PromptEditorDialog } from "./adminComposerPrompts/PromptEditorDialog";
import { PromptPreviewDialog } from "./adminComposerPrompts/PromptPreviewDialog";

const AdminComposerPrompts = () => {
  const { isAdmin } = useUserRole();
  const [prompts, setPrompts] = useState<ExcerptusPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ExcerptusPrompt | null>(null);
  const [preview, setPreview] = useState<ExcerptusPrompt | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      setPrompts(await fetchAllExcerptusPrompts());
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin]);

  const handleDelete = async (p: ExcerptusPrompt) => {
    if (!confirm(`Delete prompt "${p.title}"?`)) return;
    try {
      await deleteExcerptusPrompt(p.id);
      toast.success("Deleted");
      void refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <>
      <AppSeo title="Composer prompts" />
      <div className="container mx-auto max-w-5xl space-y-6 p-6">
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to Admin
            </Link>
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> New prompt
          </Button>
        </div>

        <div>
          <h1 className="text-2xl font-semibold">Composer prompts (Excerptus catalog)</h1>
          <p className="text-sm text-muted-foreground">
            Curate the global prompts library shown to all users in Composer → Vault → Prompts.
          </p>
        </div>

        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : prompts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No prompts yet — create one above.</p>
        ) : (
          <div className="grid gap-3">
            {prompts.map((p) => (
              <Card key={p.id}>
                <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    {p.title}
                    <Badge variant={p.is_published ? "default" : "outline"} className="text-[10px]">
                      {p.is_published ? "Published" : "Draft"}
                    </Badge>
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPreview(p)}>
                      <Eye className="h-3.5 w-3.5" /> Preview as user
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(p)}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(p)}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {p.description && (
                    <p className="mb-2 text-sm text-muted-foreground">{p.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {p.model_hint && (
                      <Badge variant="secondary" className="text-[10px]">
                        {p.model_hint}
                      </Badge>
                    )}
                    {(p.tags ?? []).map((t) => (
                      <Badge key={t} variant="secondary" className="text-[10px]">
                        {t}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <PromptEditorDialog
          open={creating || Boolean(editing)}
          onOpenChange={(o) => {
            if (!o) {
              setCreating(false);
              setEditing(null);
            }
          }}
          prompt={editing}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void refresh();
          }}
        />

        <PromptPreviewDialog prompt={preview} onOpenChange={() => setPreview(null)} />
      </div>
    </>
  );
};

export default AdminComposerPrompts;
